import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put, errorMessage } from '@/shared/api/client';
import { Alert, Button, Card, Field, Input, Skeleton, Table } from '@/shared/ui';
import { useToast } from '@/app/providers';
import { formatDateTime } from '@/shared/lib/format';

type Setting = { key: string; value: unknown; valid_from: string | null; reason: string | null; type: 'int' | 'bool'; min?: number; max?: number; desc: string };

/** Administración → Configuración → Parámetros (versionados: cada cambio abre una vigencia con motivo). */
export function SettingsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['admin', 'settings'], queryFn: () => get<Setting[]>('/admin/settings') });
  const [editing, setEditing] = useState<Setting | null>(null);
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: ({ key, v, r }: { key: string; v: unknown; r: string }) => put(`/admin/settings/${key}`, { value: v, reason: r }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'settings'] }); setEditing(null); toast.notify({ title: 'Parámetro actualizado' }); },
    onError: (e) => toast.notify({ title: 'No se pudo guardar', description: errorMessage(e), kind: 'error' }),
  });
  const submit = () => {
    if (!editing) return;
    const v = editing.type === 'bool' ? value === 'true' : Number.parseInt(value, 10);
    save.mutate({ key: editing.key, v, r: reason });
  };
  return (
    <>
      <h1 style={{ marginBottom: 'var(--sp-5)' }}>Parámetros</h1>
      <Card>
        {q.isPending && <Skeleton height={120} />}
        {q.isError && <Alert kind="error">{errorMessage(q.error)}</Alert>}
        {q.data && (
          <Table>
            <thead><tr><th>Parámetro</th><th>Valor vigente</th><th>Desde</th><th>Motivo</th><th></th></tr></thead>
            <tbody>
              {q.data.map((s) => (
                <tr key={s.key}>
                  <td><code style={{ fontSize: 'var(--fs-xs)' }}>{s.key}</code><div style={{ fontSize: 'var(--fs-xs)', color: 'var(--sgr-text-muted)' }}>{s.desc}</div></td>
                  <td className="num"><strong>{String(s.value)}</strong></td>
                  <td>{formatDateTime(s.valid_from)}</td>
                  <td style={{ fontSize: 'var(--fs-xs)' }}>{s.reason ?? '—'}</td>
                  <td><Button size="sm" variant="secondary" onClick={() => { setEditing(s); setValue(String(s.value)); setReason(''); }}>Cambiar</Button></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      {editing && (
        <Card title={`Nueva vigencia: ${editing.key}`} className="mt">
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--sgr-text-secondary)' }}>El valor anterior se conserva en el historial; los cálculos ya realizados no se recalculan (§2 p.9).</p>
          <Field label="Valor" htmlFor="val" hint={editing.type === 'bool' ? 'true / false' : `Entero${editing.min !== undefined ? ` entre ${editing.min} y ${editing.max}` : ''}`}>
            {editing.type === 'bool' ? (
              <select id="val" value={value} onChange={(e) => setValue(e.target.value)} style={{ height: 40 }}><option value="true">true</option><option value="false">false</option></select>
            ) : (
              <Input id="val" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))} />
            )}
          </Field>
          <Field label="Motivo (obligatorio, queda en auditoría)" htmlFor="reason"><Input id="reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} /></Field>
          <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
            <Button onClick={submit} loading={save.isPending} disabled={reason.trim().length < 3 || value === ''}>Guardar</Button>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancelar</Button>
          </div>
        </Card>
      )}
    </>
  );
}
